import { destination } from '@turf/turf';
import { fromLonLat } from 'ol/proj';

/**
 * OpenLayers 맵에서 Extent(범위) 계산 유틸리티 함수들
 */
class ExtentUtils {
  /**
   * FOV(Field of View) 계산
   * @param {number} sensorSize - 센서 크기 (mm)
   * @param {number} focalLength - 초점 거리 (mm)
   * @returns {number} FOV (라디안)
   */
  static calculateFOV(sensorSize, focalLength) {
    return 2 * Math.atan(sensorSize / (focalLength * 2));
  }

  /**
   * 지상 거리 계산
   * @param {number} altitude - 고도 (m)
   * @param {number} fov - FOV (라디안)
   * @returns {number} 지상 거리 (m)
   */
  static calculateGroundDistance(altitude, fov) {
    return 2 * altitude * Math.tan(fov / 2);
  }

  /**
   * 사진 촬영 범위의 Extent 계산
   * @param {Object} position - 중심 좌표 { lat, lng }
   * @param {number} aglAltitude - 지상 고도 (m)
   * @param {number} focalLength - 초점 거리 (mm)
   * @param {Object} sensorSize - 센서 크기 { width, height } (mm)
   * @returns {Array} [minX, minY, maxX, maxY] Web Mercator 좌표계
   */
  static calculatePhotoExtent(position, aglAltitude, focalLength, sensorSize) {
    // 세로/가로 촬영 범위 계산 (미터)
    const verticalDistance = ExtentUtils.calculateGroundDistance(
      aglAltitude,
      ExtentUtils.calculateFOV(sensorSize.height, focalLength)
    );
    const horizontalDistance = ExtentUtils.calculateGroundDistance(
      aglAltitude,
      ExtentUtils.calculateFOV(sensorSize.width, focalLength)
    );

    // 반경 계산 (km)
    const halfVertical = verticalDistance / 2000;
    const halfHorizontal = horizontalDistance / 2000;
    const halfDiagonal = Math.sqrt(halfVertical ** 2 + halfHorizontal ** 2);

    const center = [position.lng, position.lat];
    const west = destination(center, halfDiagonal, -90).geometry.coordinates;
    const east = destination(center, halfDiagonal, 90).geometry.coordinates;
    const south = destination(center, halfDiagonal, 180).geometry.coordinates;
    const north = destination(center, halfDiagonal, 0).geometry.coordinates;

    // Web Mercator 좌표로 변환
    const westCoord = fromLonLat(west);
    const eastCoord = fromLonLat(east);
    const southCoord = fromLonLat(south);
    const northCoord = fromLonLat(north);

    // [minX, minY, maxX, maxY]
    return [westCoord[0], southCoord[1], eastCoord[0], northCoord[1]];
  }
}

export default ExtentUtils;
